export type SearchLocale = "zh-Hans" | "en";

export type SearchEntry = {
  href: string;
  title: string;
  summary: string;
  kind: string;
  tags: string[];
};

const SEARCH_INDEX: Record<SearchLocale, SearchEntry[]> = {
  "zh-Hans": [
    {
      href: "/",
      title: "首页",
      summary: "CALL ANYTHING 的品牌定位、Hotline 协议叙事、Marketplace 和超级个体商业模型总览。",
      kind: "品牌总览",
      tags: ["首页", "hotline", "协议", "marketplace", "opc", "超级个体"],
    },
    {
      href: "/docs/",
      title: "Docs 文档中心",
      summary: "快速开始、协议规范、Platform API、自托管和 Hotline 开发入口。",
      kind: "文档入口",
      tags: ["docs", "文档", "协议", "api", "自托管", "开发"],
    },
    {
      href: "/docs/quick-start-caller/",
      title: "Caller Quick Start",
      summary: "跑通一条本地 Caller 闭环，发起一次真实调用并解读 result_package。",
      kind: "快速开始",
      tags: ["caller", "quick start", "result_package", "本地调用", "catalog"],
    },
    {
      href: "/docs/quick-start-responder/",
      title: "Responder Quick Start",
      summary: "启用 Responder、验证 example hotline、生成 registration draft 并理解 review payload。",
      kind: "快速开始",
      tags: ["responder", "quick start", "registration", "review payload", "schema"],
    },
    {
      href: "/docs/protocol-examples/",
      title: "协议最小示例",
      summary: "集中查看最小 request、成功 result_package、错误返回和 hotline registration payload。",
      kind: "协议证据页",
      tags: ["示例", "request", "result_package", "error", "registration", "payload"],
    },
    {
      href: "/docs/protocol-architecture/",
      title: "协议架构",
      summary: "集中解释 Protocol、Client、Platform、Selfhost 以及 control plane / data plane 的边界。",
      kind: "架构页",
      tags: ["architecture", "control plane", "data plane", "platform", "selfhost", "transportadapter"],
    },
    {
      href: "/docs/deployability-profiles/",
      title: "部署 Profiles",
      summary: "解释 Local Agent Loop、Selfhost Platform、Public Stack、Management Console 的启动命令、ready now / planned 状态和 secrets 安全边界。",
      kind: "部署页",
      tags: ["deployability", "selfhost", "public-stack", "console", "secrets", "preflight"],
    },
    {
      href: "/glossary/",
      title: "Glossary 术语表",
      summary: "Hotline、Caller、Responder、result_package、Template Bundle 等定义。",
      kind: "定义页",
      tags: ["glossary", "术语", "hotline", "caller", "responder", "template bundle"],
    },
    {
      href: "/faq/",
      title: "FAQ 常见问题",
      summary: "关于 MCP、Skill、API、结算、信任、上架和自托管的聚合问答。",
      kind: "FAQ",
      tags: ["faq", "mcp", "skill", "api", "结算", "信任"],
    },
    {
      href: "/compare/hotline-vs-mcp/",
      title: "Hotline vs MCP",
      summary: "解释 Hotline 为什么不是 MCP Server 的重命名。",
      kind: "对比页",
      tags: ["compare", "mcp", "hotline vs mcp", "对比"],
    },
    {
      href: "/compare/hotline-vs-api/",
      title: "Hotline vs API",
      summary: "解释 Hotline 与传统 API / SaaS 的调用模型和商业边界差异。",
      kind: "对比页",
      tags: ["compare", "api", "saas", "对比"],
    },
    {
      href: "/marketplace/",
      title: "Marketplace",
      summary: "公开 Hotline 目录、模板包、示例输入输出和能力发现入口。",
      kind: "目录页",
      tags: ["marketplace", "目录", "发现", "catalog", "template bundle"],
    },
    {
      href: "/pricing/",
      title: "Pricing 定价",
      summary: "按次调用、elapsed_ms、result_package 成本字段和预览期结算规则。",
      kind: "定价页",
      tags: ["pricing", "结算", "cost", "elapsed_ms", "result_package"],
    },
  ],
  en: [
    {
      href: "/en/",
      title: "English Home",
      summary: "English overview of the Hotline protocol, Marketplace model, and operator-owned capability framing.",
      kind: "Overview",
      tags: ["home", "hotline", "protocol", "marketplace", "operator"],
    },
    {
      href: "/en/docs/",
      title: "Docs Overview",
      summary: "English docs entrypoint for quick starts, compare pages, evidence pages, and protocol context.",
      kind: "Docs",
      tags: ["docs", "overview", "protocol", "quick start"],
    },
    {
      href: "/en/docs/quick-start-caller/",
      title: "Caller Quick Start",
      summary: "Run one local caller-side loop and inspect a real result_package shape.",
      kind: "Quick Start",
      tags: ["caller", "quick start", "result_package", "local mode", "catalog"],
    },
    {
      href: "/en/docs/quick-start-responder/",
      title: "Responder Quick Start",
      summary: "Create the first Hotline draft, inspect registration fields, and understand the review payload.",
      kind: "Quick Start",
      tags: ["responder", "quick start", "registration", "review payload", "schema"],
    },
    {
      href: "/en/docs/protocol-examples/",
      title: "Protocol Examples",
      summary: "Minimal request, successful result_package, common error shape, and Hotline registration payload in one place.",
      kind: "Evidence",
      tags: ["examples", "request", "result_package", "error", "registration", "payload"],
    },
    {
      href: "/en/docs/protocol-architecture/",
      title: "Protocol Architecture",
      summary: "Understand how Protocol, Client, Platform, and Selfhost split control plane and data plane.",
      kind: "Architecture",
      tags: ["architecture", "control plane", "data plane", "platform", "selfhost", "transportadapter"],
    },
    {
      href: "/en/docs/deployability-profiles/",
      title: "Deployability Profiles",
      summary: "Operator-readable Local Agent Loop, Selfhost Platform, Public Stack, and Management Console paths with ready now / planned state and secrets safety boundaries.",
      kind: "Deployability",
      tags: ["deployability", "selfhost", "public-stack", "console", "secrets", "preflight"],
    },
    {
      href: "/en/case-studies/caller-team-local-loop/",
      title: "Caller Case Study",
      summary: "Non-fictional proof page for the first local caller-side Hotline loop.",
      kind: "Case Study",
      tags: ["case study", "caller", "local loop", "evidence"],
    },
    {
      href: "/en/case-studies/responder-operator-first-hotline/",
      title: "Responder Case Study",
      summary: "Non-fictional proof page for turning one private workflow into a reviewable Hotline draft.",
      kind: "Case Study",
      tags: ["case study", "responder", "registration", "evidence"],
    },
    {
      href: "/en/glossary/",
      title: "Glossary",
      summary: "Canonical definitions for Hotline, Caller, Responder, result_package, and Template Bundle.",
      kind: "Definitions",
      tags: ["glossary", "definitions", "hotline", "result_package", "template bundle"],
    },
    {
      href: "/en/faq/",
      title: "FAQ",
      summary: "English FAQ covering MCP, Skills, APIs, trust, publishing, and self-hosting.",
      kind: "FAQ",
      tags: ["faq", "mcp", "skills", "api", "trust", "self-hosting"],
    },
    {
      href: "/en/compare/hotline-vs-mcp/",
      title: "Hotline vs MCP",
      summary: "Why CALL ANYTHING is not the same thing as an MCP server.",
      kind: "Compare",
      tags: ["compare", "mcp", "hotline vs mcp"],
    },
    {
      href: "/en/compare/hotline-vs-api/",
      title: "Hotline vs API",
      summary: "Why Hotline differs from raw APIs and vendor-shaped capability wrappers.",
      kind: "Compare",
      tags: ["compare", "api", "saas", "hotline vs api"],
    },
    {
      href: "/marketplace/",
      title: "Marketplace",
      summary: "Public Hotline catalog, template bundle previews, and discovery surface.",
      kind: "Marketplace",
      tags: ["marketplace", "catalog", "discovery", "template bundle"],
    },
  ],
};

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function buildTerms(query: string) {
  const normalized = normalize(query);
  if (!normalized) return [];
  const splitTerms = normalized.split(/[\s,./_|-]+/).filter(Boolean);
  return Array.from(new Set([normalized, ...splitTerms]));
}

function scoreEntry(entry: SearchEntry, terms: string[]) {
  const title = normalize(entry.title);
  const summary = normalize(entry.summary);
  const tags = entry.tags.map(normalize);
  let score = 0;

  for (const term of terms) {
    if (title.includes(term)) score += 6;
    if (summary.includes(term)) score += 3;
    if (tags.some((tag) => tag.includes(term))) score += 4;
  }

  return score;
}

export function getSearchSuggestions(locale: SearchLocale) {
  return locale === "en"
    ? ["result_package", "Hotline vs MCP", "review payload", "local mode", "Template Bundle"]
    : ["result_package", "Hotline vs MCP", "review payload", "本地调用", "Template Bundle"];
}

export function searchSiteResources(locale: SearchLocale, query: string) {
  const entries = SEARCH_INDEX[locale];
  const terms = buildTerms(query);
  if (terms.length === 0) return [];

  return entries
    .map((entry) => ({ entry, score: scoreEntry(entry, terms) }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title))
    .map((item) => item.entry);
}
